import { css } from '@emotion/react';
import { Col, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Button } from '@/components/button';
import { PlusOutlined } from '@ant-design/icons';
import DishByListTable from '@/components/tables/dish-management/dish-by-list.table';
import DishManagementTable from '@/components/tables/dish-management.table';
import { PermissionMode } from '@/enums/mode.enum';
import { setMessages } from '@/redux/slices/app.slice';
import {
  getListDishSalt,
  getListDishAge,
  getListDishCookingMethod,
  getListSourceDish,
  getListStructureMealDish,
  getListSizeDish,
  getListDishRegion,
  getListDishTag,
  exportData,
  watchDishMessage,
  resetMessages
} from '@/redux/slices/dish.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';

export default function DishManagement() {
  const [mode, setMode] = useState('SINGLE');
  const dispatch = useAppDispatch();
  const [searchFilters, setSearchFilters] = useState<FilterDish>({});
  const messageInfo = useAppSelector(watchDishMessage);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { push } = useQuery();

  useEffect(() => {
    dispatch(getListDishSalt());
    dispatch(getListDishAge());
    dispatch(getListDishCookingMethod());
    dispatch(getListSourceDish());
    dispatch(getListStructureMealDish());
    dispatch(getListSizeDish());
    dispatch(getListDishRegion());
    dispatch(getListDishTag());
  }, [dispatch]);

  const handleSearch = (filters: Record<string, any>) => {
    setSearchFilters(filters);
  };

  function mapSearchToFilters(values: any) {
    return {
      filters: {
        name: values.name || '',
        external_dish_type_ids: values.sourceDish || [],
        meal_structure_ids: values.structureMealDish || [],
        cooking_method_ids: values.dishCookingMethod || [],
        age_group_ids: values.dishAge || [],
        tag_names: values.dishLabel || [],
        region_ids: values.region || [],
        formula_types: values.saltRecipe || [],
        update_from_date: values.updateTime?.[0]?.format('YYYY-MM-DD') || null,
        update_to_date: values.updateTime?.[1]?.format('YYYY-MM-DD') || null
      }
    }
  }
  const onAdd = () => {
    push(`${PageURLs.ofDishManagement()}?mode=${PermissionMode.CREATE}`);
    setMode('CREATE_SELF');
  }

  const groupData = () => {
    push(`${PageURLs.ofDishManagement()}?mode=${PermissionMode.GROUP}`);
    setMode('CREATE_BY_LIST');
  }

  const handleExportData = async () => {
    try {
      const payload = {
        ...mapSearchToFilters(searchFilters),
        page: currentPage,
        limit: pageSize
      };
      const blob = await dispatch(exportData(payload)).unwrap();

      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Form xuất danh sách món ăn.xlsx');
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export thất bại:', err);
    }
  };

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);
  return (
    <div css={rootStyles}>
      {!mode.match('CREATE') && (
        <>
          <Row css={boxStyles} justify='space-between' align='middle'>
            <Col>
              <Typography.Title css={titleStyles} level={5}>
                Quản lý món ăn
              </Typography.Title>
            </Col>
            {mode === 'SINGLE' && (
              <Col>
                <Row justify='space-between' css={{ gap: '10px' }}>
                  <Button
                    icon={<PlusOutlined />}
                    iconPosition='start'
                    onClick={onAdd}
                    css={selfInputButtonStyles}
                  >
                    Tự nhập từng món ăn
                  </Button>
                  <Button
                    onClick={groupData}
                    css={listInputButtonStyles}
                  >
                    Nhập món ăn mới theo danh sách
                  </Button>
                  <Button
                    onClick={handleExportData}
                  >Xuất dữ liệu danh sách món ăn</Button>
                </Row>
              </Col>
            )}
          </Row>
        </>
      )}

      {mode === 'SINGLE' && <DishManagementTable
        onSearchExport={handleSearch}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />}
      {mode === 'CREATE_BY_LIST' && (
        <DishByListTable />
      )}
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

const selfInputButtonStyles = css`
  color: var(--blue-light-color);
  border-color: var(--blue-light-color);

  &:hover {
    background-color: var(--white-color) !important;
    color: var(--blue-light-color) !important;
    border-color: var(--blue-light-color) !important;
  }
`;

const listInputButtonStyles = css`
  background-color: var(--success-color);
  color: var(--white-color);
  border-color: var(--success-color);

  &:hover {
    background-color: var(--success-color) !important;
    border-color: var(--success-color) !important;
  }
`;
